import { inject, Injectable } from '@angular/core';
import { SharedLanguageService } from '@language';
import { ScanItem, ScanItemState } from '../entities/scan-item.entity';
import { ExtractContextReasons } from '../../helpers';
import { ScanItemValidationOptions } from '../entities/scan-item-validation-options.entity';
import { ApiFragment } from '../../record/entities/api-fragment.entity';
import { RelationType } from '../../record/entities/metadata.entity';

@Injectable()
export class ScanItemValidationService {
    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);

    validate(item: ScanItem, options: ScanItemValidationOptions): ScanItem {
        if (item.record == undefined) {
            return {
                ...item,
                state: ScanItemState.Rejected,
                errorMsg: this.translateService.instant('components.scanner-input.not-found')
            };
        }

        if (options.expectedRecordType != undefined && item.record?.getRecordType() !== options.expectedRecordType) {
            return {
                ...item,
                state: ScanItemState.Rejected,
                errorMsg: this.translateService.instant('components.scanner-input.wrong-record-type', {
                    expectedRecordType: this.translateService
                        .instant('overrides.RecordType.' + options.expectedRecordType)
                        .toLowerCase()
                })
            };
        }
        if (!item.record.allowedByContext(options.contextAction)) {
            return {
                ...item,
                state: ScanItemState.Rejected,
                errorMsg: ExtractContextReasons(
                    item.record,
                    options.contextAction,
                    this.translateService.instant(
                        options.missingContextActionErrorMsgKey || 'components.scanner-input.missing-context-action',
                        {
                            type: this.translateService.instant(
                                `records.record-type.refer.${item.record?.getRecordType()}`
                            )
                        }
                    )
                )
            };
        }
        if (
            options.expectedTransport != undefined &&
            !this.matchesExpectedTransport(item.record, options.expectedTransport)
        ) {
            return {
                ...item,
                state: ScanItemState.Rejected,
                errorMsg: this.translateService.instant('components.scanner-input.wrong-transport', {
                    recordType: this.translateService.instant(
                        `records.record-type.refer.${item.record?.getRecordType()}`
                    )
                })
            };
        }
        if (
            options.expectedRecordStatus != undefined &&
            item.record.getRecordStatus() !== options.expectedRecordStatus
        ) {
            return {
                ...item,
                state: ScanItemState.Rejected,
                errorMsg: this.translateService.instant('components.scanner-input.wrong-record-status', {
                    expectedRecordStatus: this.translateService
                        .instant('overrides.RecordStatus.' + options.expectedRecordStatus)
                        .toLowerCase()
                })
            };
        }
        return {
            ...item,
            state: ScanItemState.Accepted
        };
    }

    private matchesExpectedTransport(record: ApiFragment, expectedTransport: ApiFragment): boolean {
        // Matches if the scanned item is the expected transport
        if (record.getFragmentId() === expectedTransport.getFragmentId()) {
            return true;
        }
        // Matches if the record is held by the expected transport (=transport unit)
        if (
            record
                .getRelation(RelationType.IsHeldBy)
                .filter(fragmentId => fragmentId === expectedTransport?.getFragmentId()).length > 0
        ) {
            return true;
        }
        // Matches if the record is held by a record (transport unit) that is held by the transport (i.e. it's a packaging unit of the transport)
        if (
            record
                .getRelation(RelationType.IsHeldBy)
                .filter(fragmentId => expectedTransport.getRelation(RelationType.Holds).includes(fragmentId)).length > 0
        ) {
            return true;
        }
        return false;
    }
}
